import { HeidiVersion, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';

import { useReverseTrial, useUserTier } from '../../../data/injected-billing';
import { useQueryUserBillingInfo } from '../../../data/query/useQueryUserBillingInfo';
import {
  applyClinicianTrialEndDevOverride,
  clinicianTrialEndDevOverrideAtom,
} from './clinician-trial-end-dev-override';
import {
  type ClinicianTrialEndState,
  deriveClinicianTrialEndState,
  deriveForcedClinicianTrialEndState,
} from './clinician-trial-end-state';

export type ClinicianTrialEndGates = {
  /** `release-growth-heidi-2-clinician-trial-end`. */
  isTrialEndEnabled: boolean;
  isAppleCompliantBuild: boolean;
};

/** Where the member is in their Heidi II Clinician trial, or `null` when the flow does not apply. */
export function useClinicianTrialEndState({
  isTrialEndEnabled,
  isAppleCompliantBuild,
}: ClinicianTrialEndGates): ClinicianTrialEndState | null {
  const { data: user } = useQueryUser();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { userTier } = useUserTier();
  const { isClinicianTrialUser, clinicianTrialEnded, clinicianTrialAvailable } =
    useReverseTrial();
  const devOverride = applyClinicianTrialEndDevOverride(
    useAtomValue(clinicianTrialEndDevOverrideAtom)
  );

  const now = new Date();
  if (devOverride != null) {
    return isAppleCompliantBuild
      ? null
      : deriveForcedClinicianTrialEndState(devOverride.trialEnd, now);
  }

  return deriveClinicianTrialEndState(
    {
      isEnabled: isTrialEndEnabled,
      isHeidi2: billingInfo?.heidi_version === HeidiVersion.Value2,
      isAppleCompliantBuild,
      isManaged: !!user?.is_managed,
      isClinicianTrialUser,
      clinicianTrialEnded,
      hadClinicianTrial: user != null && !clinicianTrialAvailable,
      isFreeTier: userTier === SubscriptionTier.FREE,
      trialEnd: billingInfo?.evidence_billing?.trial_end_date,
    },
    now
  );
}
